import { resetDialogConfirm } from 'feature/hooks/dialogSlice';
import authFetch, { checkForUnauthorizedResponse } from '../../utils/axios';

import { IClaim, REASON_CODE_TYPOLOGY } from 'types/Claim';
import { IClaimStatusUpdatePayload, IReturnStatus } from 'types/Claims';
import { ThunkApiType } from 'types/utils';

export const getClaimDetailsThunk = async (
	claimUUID: string,
	thunkAPI: ThunkApiType,
) => {
	let getClaimDetailsURL = `/claim/${claimUUID}/complete`;
	try {
		const response = await authFetch.get(getClaimDetailsURL);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const calculateEstimatesThunk = async (
	formData: IClaim,
	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.post('/claim/calculateCost', formData);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const updateClaimStatusThunk = async (
	{
		claimUuids,
		statusId,
		reasonCode,
		note,
		denialArticle,
		denialNumArticle,
	}: IClaimStatusUpdatePayload,
	thunkAPI: ThunkApiType,
) => {
	const updateClaimStatusURL = `/claim`;
	try {
		const payload: IClaimStatusUpdatePayload = {
			claimUuids: claimUuids,
			statusId,
		};

		if (reasonCode && note) {
			payload.reasonCode = reasonCode;
			payload.note = note;
		}
		if (denialArticle && denialNumArticle) {
			payload.denialArticle = denialArticle;
			payload.denialNumArticle = denialNumArticle;
		}

		const response = await authFetch.put(updateClaimStatusURL, payload);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const createComplaintThunk = async (
	{
		claimUuid,
		date,
	}: {
		claimUuid: string;
		date: string;
	},

	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.post(`/complaints`, {
			claimUuid,
			complaintDate: date,
		});
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const createFraudThunk = async (
	{
		claimUuid,
		date,
	}: {
		claimUuid: string;
		date: string;
	},

	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.post(`/frauds`, {
			claimUuid,
			fraudDate: date,
		});
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const deleteComplaintThunk = async (
	_: string,
	thunkAPI: ThunkApiType,
) => {
	const { claimData } = thunkAPI.getState().claim;
	try {
		const response = await authFetch.delete(
			`/complaints/claims/${claimData.claimUUID}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const deleteFraudThunk = async (_: string, thunkAPI: ThunkApiType) => {
	const { claimData } = thunkAPI.getState().claim;
	try {
		const response = await authFetch.delete(
			`/frauds/claims/${claimData.claimUUID}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const updateComplaintThunk = async (
	{
		claimUuid,
		date,
	}: {
		claimUuid: string;
		date: string;
	},

	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.put(`/complaints`, {
			claimUuid,
			complaintDate: date,
		});
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const updateFraudThunk = async (
	{
		claimUuid,
		date,
	}: {
		claimUuid: string;
		date: string;
	},

	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.put(`/frauds`, {
			claimUuid,
			fraudDate: date,
		});
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const rekeyClaimThunk = async (
	{
		uuid,
		formData,
	}: {
		uuid: string;
		formData: IClaim;
	},

	thunkAPI: ThunkApiType,
) => {
	const rekeyClaimURL = `/claim/${uuid}/rekey`;
	try {
		const response = await authFetch.post(rekeyClaimURL, formData);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getReasonCodeVoidThunk = async (
	_: object,
	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.get(
			`/reasonCode?typology=${REASON_CODE_TYPOLOGY.VOID}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getReasonCodeCanceledThunk = async (
	_: object,
	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.get(
			`/reasonCode?typology=${REASON_CODE_TYPOLOGY.CANCEL}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getReasonCodePendingThunk = async (
	_: object,
	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.get(
			`/reasonCode?typology=${REASON_CODE_TYPOLOGY.PENDING}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const getReasonCodeRejectThunk = async (
	_: object,
	thunkAPI: ThunkApiType,
) => {
	try {
		const response = await authFetch.get(
			`/reasonCode?typology=${REASON_CODE_TYPOLOGY.REJECT}`,
		);
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const voidClaimThunk = async (
	claimUUID: string,
	thunkAPI: ThunkApiType,
) => {
	const voidClaimURL = `/claim/${claimUUID}/void`;

	const { reasonCode, reasonNote } = thunkAPI.getState().input;

	const body = {
		reasonCode,
		note: reasonNote,
	};

	try {
		const response = await authFetch.post(voidClaimURL, body);
		thunkAPI.dispatch(resetDialogConfirm());
		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const sendToReviewThunk = async (_: string, thunkAPI: ThunkApiType) => {
	const { claimData } = thunkAPI.getState().claim;

	const { retailNetOfTax, reviewConsent, internalNote, ...receiptData } =
		claimData.receipt;

	const body = {
		receipt: {
			...receiptData,
			totalInvoiceAmount: Number(claimData.receipt.totalInvoiceAmount),
		},
		attachments: {
			prescription: claimData.attachments.prescription,
			receipt: claimData.attachments.receipt,
		},
		retailNetOfTax: Number(retailNetOfTax),
		reviewConsent,
		internalNote,
	};

	try {
		await authFetch.post(`/claim/${claimData.claimUUID}/review`, body);
		thunkAPI.dispatch(resetDialogConfirm());

		return claimData.claimUUID;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const returnClaimThunk = async (
	action: IReturnStatus['code'],
	thunkAPI: ThunkApiType,
) => {
	const { claimData } = thunkAPI.getState().claim;

	const returnClaimURL = `returns/claims/${claimData.claimUUID}/status`;

	const body = {
		statusOperation: action,
	};

	try {
		const response = await authFetch.post(returnClaimURL, body);

		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};

export const updateClaimCommentThunk = async (
	{ comments }: { comments: string | null },
	thunkAPI: ThunkApiType,
) => {
	const { claimData } = thunkAPI.getState().claim;

	const updateClaimCommentURL = `claim/${claimData.claimUUID}/comments`;

	const body = {
		comments,
	};

	try {
		const response = await authFetch.patch(updateClaimCommentURL, body);

		return response.data;
	} catch (error: ThunkApiType) {
		return checkForUnauthorizedResponse(error, thunkAPI);
	}
};
